<script lang="ts">
	import type { OrderProduct } from "../../../../core/schema/OrderProduct.js";
	import { OrderStatus } from "../../../../core/schema/OrderStatus.js";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import { formatPrice } from "../../../../core/utils/formatPrice.js";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import ConfigPill from "../ConfigPill.svelte";
	import { creamColorNameMap } from "../../../../core/schema/CreamColor.js";
	import {
		MacaroonVariant,
		macaroonVariantKeyMap,
		macaroonVariantNameMap,
	} from "../../../../core/schema/MacaroonVariant.js";
	import { minMacaroons } from "../../../../core/schema/productOptionsConstants.js";
	import cislodort from "../../../../core/assets/icons/cislodort.svg?raw";
	import ovocenavic from "../../../../core/assets/icons/ovocenavic.svg?raw";
	import bezlepek from "../../../../core/assets/icons/bezlepek.svg?raw";
	import bezlaktoza from "../../../../core/assets/icons/bezlaktoza.svg?raw";
	import pusinky from "../../../../core/assets/icons/pusinky.svg?raw";
	import cakeConf from "../../../../core/assets/icons/cakeconf.svg?raw";
	import macaroonIcon from "../../../../core/assets/icons/macaroon.svg?raw";
	import cedulkydort from "../../../../core/assets/icons/cedulkydort.svg?raw";
	import type { ProductForCustomer } from "../../../../core/schema/Product.js";
	import { shouldShowProductForCustomerSize } from "../../../../core/schema/utils/shouldShowProductForCustomerSize.js";
	import { ProductSizeLabelType } from "../../../../core/schema/ProductSizeLabelType.js";
	import { formatNumber } from "../../../../core/utils/formatNumber.js";
	import { productSizeLabelTypeShorthandNameMap } from "../../../../core/schema/names/productSizeLabelTypeShorthandNameMap.js";
	import { productSizeKeyMap } from "../../../../core/schema/productSizeKeyMap.js";
	import { productSizeNameMap } from "../../../../core/schema/names/productSizeNameMap.js";
	import { doesOrderProductHaveAnyOption } from "../../../../core/schema/utils/doesOrderProductHaveAnyOption.js";

	export let product: ProductForCustomer;
	export let orderProduct: OrderProduct;
	export let orderStatus: OrderStatus;
	export let isAlone: boolean;

	const globalSettings = getGlobalSettings();
	const { TENANT_LOCALE } = globalSettings;
</script>

<div class="flex items-baseline justify-between">
	{#if shouldShowProductForCustomerSize(product, orderProduct)}
		{@const customSize =
			orderProduct.product.sizeLabel && orderProduct.product.sizeLabel.type !== ProductSizeLabelType.Serving}
		<div class="flex items-center gap-3">
			<p class="text-2xs md:text-xs">Velikost</p>
			<div
				class="border-1 flex h-7 items-center justify-center rounded-full border-gray-200"
				class:aspect-square={!customSize}
				class:px-2={customSize}
			>
				<div class="text-2xs font-semibold">
					{#if orderProduct.product.sizeLabel && orderProduct.product.sizeLabel.type !== ProductSizeLabelType.Serving}
						{@const value = orderProduct.product.sizeLabel.values[productSizeKeyMap[orderProduct.size]]}
						<div class="flex gap-0.5">
							<span>{formatNumber(value, TENANT_LOCALE, false)}</span>
							<span>{productSizeLabelTypeShorthandNameMap[orderProduct.product.sizeLabel.type]}</span>
						</div>
					{:else}
						<span>{productSizeNameMap[orderProduct.size][TENANT_LOCALE]}</span>
					{/if}
				</div>
			</div>
		</div>
	{/if}
	{#if ![OrderStatus.Delivered, OrderStatus.Cancelled].includes(orderStatus) && !isAlone}
		<p class="ml-auto font-bold lg:hidden">
			{nbspify(formatPrice(orderProduct.product.price.withTax, TENANT_LOCALE, false))}
		</p>
	{/if}
</div>

{#if doesOrderProductHaveAnyOption(orderProduct.options)}
	<div class="flex flex-wrap gap-1 md:gap-x-[.35rem]">
		<p class="text-2xs md:text-xs">Úpravy</p>

		{#if orderProduct.options.numbers.length > 0}
			{#each orderProduct.options.numbers as { number }}
				<ConfigPill name="Číslo na dort" icon={cislodort} value={number} />
			{/each}
		{/if}
		{#if orderProduct.options.extraFruit}
			<ConfigPill name="Ovoce navíc" icon={ovocenavic} />
		{/if}
		{#if orderProduct.options.glutenFree}
			<ConfigPill name="Bezlepková varianta" icon={bezlepek} />
		{/if}
		{#if orderProduct.options.lactoseFree}
			<ConfigPill name="Bez laktózy" icon={bezlaktoza} />
		{/if}
		{#if orderProduct.options.meringues}
			<ConfigPill name="Sněhové pusinky" icon={pusinky} />
		{/if}
		{#if orderProduct.options.creamColor}
			<ConfigPill
				name="Barevná šlehačka"
				icon={cakeConf}
				value={creamColorNameMap[orderProduct.options.creamColor][TENANT_LOCALE]}
			/>
		{/if}
		{#if orderProduct.options.macaroons}
			{#each Object.values(MacaroonVariant) as variant}
				{@const key = macaroonVariantKeyMap[variant]}
				{@const amount = orderProduct.options.macaroons[key]}
				{#if amount >= minMacaroons}
					<ConfigPill
						name="Makronka"
						icon={macaroonIcon}
						value="{macaroonVariantNameMap[variant][TENANT_LOCALE]} ({amount}ks)"
					/>
				{/if}
			{/each}
		{/if}
		{#if orderProduct.options.labels.length > 0}
			{#each orderProduct.options.labels as label, index}
				<ConfigPill name="Cedulka" icon={cedulkydort} {index} value={label.text} />
			{/each}
		{/if}
	</div>
{/if}
